<script setup lang="ts">
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  Settings,
  User,
} from "lucide-vue-next";
import type { Locale } from "~/constants";

const router = useRouter();
const route = useRoute();
const localeRoute = useLocaleRoute();
const { t, locale } = useI18n();
const { user, loggedIn, logout } = useAuth();
const toast = useToast();

const isLogoutDialogOpen = ref(false);
const isLogoutSubmitting = ref(false);
const isMobileMenuOpen = ref(false);
const isMobileLanguageOpen = ref(false);

const languages: {
  label: string;
  value: Locale;
  flag: string;
}[] = [
  { label: "Lietuvių", value: "lt", flag: "/icons/flags/lt.svg" },
  { label: "English", value: "en", flag: "/icons/flags/en.svg" },
  { label: "Русский", value: "ru", flag: "/icons/flags/ru.svg" },
];

const userFullName = computed(() => {
  if (!user.value) return "";

  return [user.value.firstName, user.value.lastName].filter(Boolean).join(" ");
});

const userInitials = computed(() => {
  if (!user.value) return "?";

  const firstName = user.value.firstName?.[0] || "";
  const lastName = user.value.lastName?.[0] || "";
  const email = user.value.email?.[0] || "";

  return `${firstName}${lastName || email}`.toUpperCase();
});

const userAvatarUrl = computed(() => {
  if (!user.value) return "";

  return user.value.profileImageUrl;
});

function closeMobileMenu() {
  isMobileMenuOpen.value = false;
  isMobileLanguageOpen.value = false;
}

async function changeLanguage(lang: Locale) {
  if (locale.value === lang) return;

  const localizedRoute = localeRoute(
    {
      path: route.path,
      query: route.query,
    },
    lang,
  );

  closeMobileMenu();

  if (localizedRoute) {
    await router.push(localizedRoute);
  }
}

function openLogoutDialog(close?: () => void) {
  close?.();
  closeMobileMenu();
  isLogoutDialogOpen.value = true;
}

async function confirmLogout() {
  try {
    isLogoutSubmitting.value = true;

    await logout();

    toast.success({
      title: t("toast.logout.success.title"),
      description: t("toast.logout.success.description"),
    });

    isLogoutDialogOpen.value = false;
  } finally {
    isLogoutSubmitting.value = false;
  }
}

watch(
  () => route.fullPath,
  () => closeMobileMenu(),
);

watch(isMobileMenuOpen, open => {
  if (!import.meta.client) return;

  document.documentElement.style.overflow = open ? "hidden" : "";
});

onBeforeUnmount(() => {
  if (!import.meta.client) return;
  document.documentElement.style.overflow = "";
});
</script>

<template>
  <div class="relative z-[1020] shrink-0 overflow-visible">
    <div class="hidden items-center gap-2.5 lg:flex">
      <LangSelect />

      <span class="mx-1 h-7 w-px shrink-0 bg-[#DCE3E1]" aria-hidden="true" />

      <template v-if="!loggedIn">
        <NuxtLink
          :to="localeRoute('/login')"
          class="flex h-10 select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#D9E1DE] bg-white px-4 text-sm font-semibold text-app-text transition duration-300 hover:border-primary/35 hover:bg-[#F7FAF9] hover:text-primary"
        >
          <User class="size-[17px] shrink-0" :stroke-width="1.9" />
          {{ $t("header.login") }}
        </NuxtLink>

        <NuxtLink
          :to="localeRoute('/register')"
          class="flex h-10 select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary px-5 text-sm font-semibold text-white shadow-[0_4px_12px_rgba(19,122,104,0.14)] transition duration-300 hover:-translate-y-px hover:bg-primary-400 hover:shadow-[0_7px_18px_rgba(19,122,104,0.18)]"
        >
          <BriefcaseBusiness class="size-[17px] shrink-0" :stroke-width="1.9" />
          {{ $t("header.signup") }}
        </NuxtLink>
      </template>

      <AppDropdown v-else content-class="w-[292px]" sheet-class="pb-5">
        <template #trigger="{ open }">
          <button
            type="button"
            class="flex h-11 shrink-0 select-none items-center justify-center gap-2 rounded-full border bg-white p-1 pr-2"
            :class="
              open
                ? 'border-primary/50 bg-primary-50'
                : 'border-app-border hover:border-primary/40'
            "
            :aria-expanded="open"
            aria-haspopup="menu"
          >
            <div
              class="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-bold text-white"
            >
              <img
                v-if="userAvatarUrl"
                :src="userAvatarUrl"
                :alt="userFullName || user?.email || 'User avatar'"
                class="size-full object-cover"
              />

              <span v-else>{{ userInitials }}</span>
            </div>

            <ChevronDown
              class="size-4 shrink-0 transition-transform duration-300"
              :class="open ? 'rotate-180 text-primary' : 'text-app-muted'"
            />
          </button>
        </template>

        <template #content="{ close }">
          <div class="px-3 pb-3 pt-2">
            <div class="flex items-center gap-3">
              <div
                class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-bold text-white"
              >
                <img
                  v-if="userAvatarUrl"
                  :src="userAvatarUrl"
                  :alt="userFullName || user?.email || 'User avatar'"
                  class="size-full object-cover"
                />
                <span v-else>{{ userInitials }}</span>
              </div>

              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-semibold text-app-text">
                  {{ userFullName || user?.email }}
                </div>
                <div
                  v-if="userFullName && user?.email"
                  class="mt-0.5 truncate text-xs text-app-muted"
                >
                  {{ user.email }}
                </div>
              </div>
            </div>
          </div>

          <div class="mx-2 border-t border-app-border" />

          <div class="py-1">
            <NuxtLink
              :to="localeRoute('/profile')"
              class="group flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-app-text transition-colors duration-300 hover:bg-primary-50 hover:text-primary"
              @click="close"
            >
              <User
                class="size-[18px] text-app-muted transition-colors duration-300 group-hover:text-primary"
                :stroke-width="1.8"
              />
              <span class="min-w-0 flex-1">{{
                $t("header.account.user_profile")
              }}</span>
              <ChevronRight
                class="size-4 text-[#8A918D] transition-colors duration-300 group-hover:text-primary"
                :stroke-width="1.8"
              />
            </NuxtLink>

            <NuxtLink
              v-if="user?.role === 'worker'"
              :to="localeRoute('/worker/profile')"
              class="group flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-app-text transition-colors duration-300 hover:bg-primary-50 hover:text-primary"
              @click="close"
            >
              <BriefcaseBusiness
                class="size-[18px] text-app-muted transition-colors duration-300 group-hover:text-primary"
                :stroke-width="1.8"
              />
              <span class="min-w-0 flex-1">{{
                $t("header.account.worker_profile")
              }}</span>
              <ChevronRight
                class="size-4 text-[#8A918D] transition-colors duration-300 group-hover:text-primary"
                :stroke-width="1.8"
              />
            </NuxtLink>

            <NuxtLink
              :to="localeRoute('/settings')"
              class="group flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-app-text transition-colors duration-300 hover:bg-primary-50 hover:text-primary"
              @click="close"
            >
              <Settings
                class="size-[18px] text-app-muted transition-colors duration-300 group-hover:text-primary"
                :stroke-width="1.8"
              />
              <span class="min-w-0 flex-1">{{
                $t("header.account.settings")
              }}</span>
              <ChevronRight
                class="size-4 text-[#8A918D] transition-colors duration-300 group-hover:text-primary"
                :stroke-width="1.8"
              />
            </NuxtLink>
          </div>

          <div class="mx-2 border-t border-app-border pt-1">
            <button
              type="button"
              class="flex min-h-11 w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm font-medium text-app-danger transition-colors duration-300 hover:bg-red-50"
              @click="openLogoutDialog(close)"
            >
              <LogOut class="size-[18px]" :stroke-width="1.8" />
              <span>{{ $t("header.logout") }}</span>
            </button>
          </div>
        </template>
      </AppDropdown>
    </div>

    <div class="flex items-center gap-1.5 lg:hidden">
      <NuxtLink
        v-if="!loggedIn"
        :to="localeRoute('/register')"
        class="flex h-10 shrink-0 select-none items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-sm font-semibold text-white transition duration-300 hover:bg-primary-400"
        :aria-label="$t('header.signup')"
      >
        <BriefcaseBusiness class="size-4 shrink-0" :stroke-width="1.9" />
        <span>{{ $t("header.signup_mobile") }}</span>
      </NuxtLink>

      <button
        type="button"
        class="flex size-10 items-center justify-center rounded-full text-app-text transition duration-300 hover:bg-gray-100"
        :aria-label="$t('header.menu')"
        :aria-expanded="isMobileMenuOpen"
        aria-controls="mobile-main-menu"
        @click="isMobileMenuOpen = true"
      >
        <Menu class="size-[22px]" :stroke-width="2" />
      </button>
    </div>

    <Teleport to="body">
      <Transition name="mobile-menu-backdrop">
        <button
          v-if="isMobileMenuOpen"
          type="button"
          class="fixed inset-0 z-[1400] bg-black/40 lg:hidden"
          :aria-label="$t('header.close_menu')"
          @click="closeMobileMenu"
        />
      </Transition>

      <Transition name="mobile-menu-panel">
        <aside
          v-if="isMobileMenuOpen"
          id="mobile-main-menu"
          class="fixed inset-x-0 bottom-0 z-[1410] flex max-h-[88dvh] flex-col overflow-hidden rounded-t-[22px] border border-black/10 bg-white shadow-2xl lg:hidden"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('header.menu')"
        >
          <div
            class="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-[#D1D5DB]"
          />

          <div
            class="overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4"
          >
            <div v-if="loggedIn" class="mb-3 px-2 py-1">
              <div class="flex items-center gap-3">
                <div
                  class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-bold text-white"
                >
                  <img
                    v-if="userAvatarUrl"
                    :src="userAvatarUrl"
                    :alt="userFullName || user?.email || 'User avatar'"
                    class="size-full object-cover"
                  />
                  <span v-else>{{ userInitials }}</span>
                </div>

                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-semibold text-app-text">
                    {{ userFullName || user?.email }}
                  </div>
                  <div
                    v-if="userFullName && user?.email"
                    class="mt-0.5 truncate text-xs text-app-muted"
                  >
                    {{ user.email }}
                  </div>
                </div>
              </div>
            </div>

            <div
              class="overflow-hidden rounded-xl border border-app-border bg-white"
            >
              <button
                type="button"
                class="flex min-h-11 w-full items-center gap-3 px-3 text-left text-sm font-semibold text-app-text transition duration-300 hover:bg-gray-50"
                :aria-expanded="isMobileLanguageOpen"
                @click="isMobileLanguageOpen = !isMobileLanguageOpen"
              >
                <img
                  :src="
                    languages.find(language => language.value === locale)?.flag
                  "
                  alt=""
                  class="h-[17px] w-[23px] shrink-0 rounded-[4px] object-cover shadow-[0_0_0_1px_rgba(17,24,39,0.08)]"
                  aria-hidden="true"
                />
                <span class="min-w-0 flex-1 truncate">
                  {{
                    languages.find(language => language.value === locale)?.label
                  }}
                </span>
                <ChevronDown
                  class="size-4 shrink-0 text-app-muted transition-transform duration-300"
                  :class="isMobileLanguageOpen ? 'rotate-180 text-primary' : ''"
                  :stroke-width="1.9"
                />
              </button>

              <Transition name="mobile-language-list">
                <div
                  v-if="isMobileLanguageOpen"
                  class="border-t border-app-border py-1"
                >
                  <button
                    v-for="language in languages.filter(
                      item => item.value !== locale,
                    )"
                    :key="language.value"
                    type="button"
                    class="flex min-h-10 w-full items-center gap-3 px-3 text-left text-sm font-medium text-app-text transition duration-300 hover:bg-gray-50 hover:text-primary"
                    @click="changeLanguage(language.value)"
                  >
                    <img
                      :src="language.flag"
                      alt=""
                      class="h-[17px] w-[23px] shrink-0 rounded-[4px] object-cover shadow-[0_0_0_1px_rgba(17,24,39,0.08)]"
                      aria-hidden="true"
                    />
                    <span class="flex-1">{{ language.label }}</span>
                  </button>
                </div>
              </Transition>
            </div>

            <div class="my-3 border-t border-app-border" />

            <template v-if="!loggedIn">
              <NuxtLink
                :to="localeRoute('/login')"
                class="flex min-h-11 items-center rounded-xl px-3 text-sm font-semibold text-app-text transition duration-300 hover:bg-gray-100"
                @click="closeMobileMenu"
              >
                {{ $t("header.login") }}
              </NuxtLink>
            </template>

            <template v-else>
              <div class="space-y-1">
                <NuxtLink
                  :to="localeRoute('/profile')"
                  class="group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium text-app-text transition duration-300 hover:bg-primary-50 hover:text-primary"
                  @click="closeMobileMenu"
                >
                  <User
                    class="size-[18px] shrink-0 text-app-muted group-hover:text-primary"
                  />
                  <span class="flex-1">{{
                    $t("header.account.user_profile")
                  }}</span>
                  <ChevronRight
                    class="size-4 text-app-muted group-hover:text-primary"
                  />
                </NuxtLink>

                <NuxtLink
                  v-if="user?.role === 'worker'"
                  :to="localeRoute('/worker/profile')"
                  class="group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium text-app-text transition duration-300 hover:bg-primary-50 hover:text-primary"
                  @click="closeMobileMenu"
                >
                  <BriefcaseBusiness
                    class="size-[18px] shrink-0 text-app-muted group-hover:text-primary"
                  />
                  <span class="flex-1">{{
                    $t("header.account.worker_profile")
                  }}</span>
                  <ChevronRight
                    class="size-4 text-app-muted group-hover:text-primary"
                  />
                </NuxtLink>

                <NuxtLink
                  :to="localeRoute('/settings')"
                  class="group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium text-app-text transition duration-300 hover:bg-primary-50 hover:text-primary"
                  @click="closeMobileMenu"
                >
                  <Settings
                    class="size-[18px] shrink-0 text-app-muted group-hover:text-primary"
                  />
                  <span class="flex-1">{{
                    $t("header.account.settings")
                  }}</span>
                  <ChevronRight
                    class="size-4 text-app-muted group-hover:text-primary"
                  />
                </NuxtLink>
              </div>

              <div class="mt-3 border-t border-app-border pt-3">
                <button
                  type="button"
                  class="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium text-app-danger transition duration-300 hover:bg-red-50"
                  @click="openLogoutDialog()"
                >
                  <LogOut class="size-[18px]" />
                  <span>{{ $t("header.logout") }}</span>
                </button>
              </div>
            </template>
          </div>
        </aside>
      </Transition>
    </Teleport>

    <AppConfirmDialog
      v-model="isLogoutDialogOpen"
      variant="danger"
      :loading="isLogoutSubmitting"
      :title="$t('header.logout_confirm.title')"
      :description="$t('header.logout_confirm.description')"
      :confirm-text="$t('header.logout_confirm.confirm')"
      :cancel-text="$t('header.logout_confirm.cancel')"
      @confirm="confirmLogout"
    />
  </div>
</template>

<style scoped>
.mobile-menu-backdrop-enter-active,
.mobile-menu-backdrop-leave-active {
  transition: opacity 180ms ease;
}

.mobile-menu-backdrop-enter-from,
.mobile-menu-backdrop-leave-to {
  opacity: 0;
}

.mobile-menu-panel-enter-active,
.mobile-menu-panel-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms ease;
}

.mobile-menu-panel-enter-from,
.mobile-menu-panel-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.mobile-language-list-enter-active,
.mobile-language-list-leave-active {
  transition:
    opacity 160ms ease,
    transform 180ms ease;
}

.mobile-language-list-enter-from,
.mobile-language-list-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
